/**
 * <cart-drawer> — slide-in drawer that uses the Shopify Cart API.
 * - Intercepts every form[action*="/cart/add"] submit on the page → POSTs async,
 *   then opens the drawer with the latest cart state.
 * - Header cart icon also opens the drawer.
 * - Quantity adjustments and item removal use /cart/change.js.
 * - Updates header cart count via window.dispatchEvent(new CustomEvent('cart:updated', ...)).
 */

const formatMoney = (cents, currency) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(cents / 100);
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.currency = this.dataset.currency || 'EUR';

    this.itemsList = this.querySelector('[data-cart-items]');
    this.emptyState = this.querySelector('[data-cart-empty]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.countEl = this.querySelector('[data-cart-count]');
    this.shippingText = this.querySelector('[data-shipping-text]');
    this.shippingFill = this.querySelector('[data-shipping-fill]');
    this.hasItemsEls = Array.from(this.querySelectorAll('[data-cart-has-items]'));
    this.upsellBlock = this.querySelector('.cart-drawer__upsell');
    this.upsellRows = Array.from(this.querySelectorAll('.cart-drawer__upsell-row'));
    this.itemsBadge = this.querySelector('[data-items-badge]');

    this.querySelectorAll('[data-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    });

    this.bindAddForms();
    this.bindUpsellButtons();
    this.refresh();
  }

  bindUpsellButtons() {
    this.querySelectorAll('[data-upsell-add]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.disabled = true;
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
          await this.refresh();
        } catch (err) {
          console.error('Upsell add failed:', err);
        } finally {
          btn.disabled = false;
        }
      });
    });
  }

  bindAddForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.dataset.loading = 'true';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          alert(err.description || 'Fehler beim Hinzufügen');
          return;
        }

        await this.refresh();
        this.open();
      } catch (err) {
        console.error('Cart add failed:', err);
      } finally {
        if (submitBtn) submitBtn.dataset.loading = 'false';
      }
    });
  }

  async refresh() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.render(cart);
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (err) {
      console.error('Cart fetch failed:', err);
    }
  }

  render(cart) {
    this.removeAttribute('hidden');
    this.countEl.textContent = String(cart.item_count);

    const isEmpty = cart.item_count === 0;
    this.hasItemsEls.forEach((el) => (el.hidden = isEmpty));
    this.emptyState.hidden = !isEmpty;

    this.itemsList.replaceChildren(...cart.items.map((item) => this.renderItem(item)));

    if (this.subtotalEl) {
      this.subtotalEl.textContent = formatMoney(cart.total_price, this.currency);
    }

    if (this.itemsBadge) {
      const label = cart.item_count === 1 ? '1 Artikel' : `${cart.item_count} Artikel`;
      this.itemsBadge.textContent = label;
    }

    this.updateShipping(cart);
    this.updateHeaderCount(cart.item_count);
    this.updateUpsells(cart);
  }

  updateUpsells(cart) {
    if (!this.upsellBlock || this.upsellRows.length === 0) return;

    const cartVariantIds = new Set(cart.items.map((i) => String(i.variant_id)));
    let visibleCount = 0;

    this.upsellRows.forEach((row) => {
      const btn = row.querySelector('[data-upsell-add]');
      const variantId = btn?.dataset.variantId;
      const inCart = variantId && cartVariantIds.has(variantId);
      row.hidden = inCart;
      if (!inCart) visibleCount += 1;
    });

    this.upsellBlock.hidden = cart.item_count === 0 || visibleCount === 0;
  }

  renderItem(item) {
    const li = document.createElement('li');
    li.className = 'cart-drawer__item';
    li.dataset.key = item.key;

    const thumb = item.image
      ? `<a class="cart-drawer__item-thumb" href="${item.url}"><img src="${item.image}" alt="${item.product_title}" width="80" height="80" loading="lazy"></a>`
      : `<span class="cart-drawer__item-thumb"></span>`;

    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<span class="cart-drawer__item-variant">${item.variant_title}</span>`
        : '';

    const wasPrice =
      item.original_line_price > item.final_line_price
        ? `<span class="cart-drawer__item-price-was">${formatMoney(item.original_line_price, this.currency)}</span>`
        : '';

    li.innerHTML = `
      ${thumb}
      <div class="cart-drawer__item-info">
        <a class="cart-drawer__item-title" href="${item.url}">${item.product_title}</a>
        ${variant}
        <div class="cart-drawer__item-qty" data-qty>
          <button type="button" data-qty-down aria-label="weniger">−</button>
          <span>${item.quantity}</span>
          <button type="button" data-qty-up aria-label="mehr">+</button>
        </div>
      </div>
      <div class="cart-drawer__item-meta">
        <button class="cart-drawer__item-remove" type="button" data-remove aria-label="entfernen">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </button>
        <span class="cart-drawer__item-price">${formatMoney(item.final_line_price, this.currency)}</span>
        ${wasPrice}
      </div>
    `;

    li.querySelector('[data-qty-down]').addEventListener('click', () =>
      this.changeQty(item.key, item.quantity - 1),
    );
    li.querySelector('[data-qty-up]').addEventListener('click', () =>
      this.changeQty(item.key, item.quantity + 1),
    );
    li.querySelector('[data-remove]').addEventListener('click', () =>
      this.changeQty(item.key, 0),
    );

    return li;
  }

  async changeQty(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refresh();
    } catch (err) {
      console.error('Cart change failed:', err);
    }
  }

  updateShipping(cart) {
    if (!this.shippingText || !this.shippingFill || this.threshold <= 0) return;

    const subtotal = cart.total_price;
    const remaining = this.threshold - subtotal;
    const pct = Math.min(100, Math.max(0, (subtotal / this.threshold) * 100));
    this.shippingFill.style.width = `${pct}%`;

    if (remaining <= 0) {
      this.shippingText.innerHTML = `🎉 <strong>Du hast GRATIS Versand!</strong>`;
    } else {
      this.shippingText.innerHTML = `Nur noch <strong>${formatMoney(remaining, this.currency)}</strong> für GRATIS Versand`;
    }
  }

  updateHeaderCount(count) {
    const link = document.querySelector('.header__cart');
    if (!link) return;
    let badge = link.querySelector('.header__cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'header__cart-count';
        badge.setAttribute('aria-hidden', 'true');
        link.appendChild(badge);
      }
      badge.textContent = String(count);
    } else if (badge) {
      badge.remove();
    }
  }

  open() {
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.setAttribute('open', ''));
    document.documentElement.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
  }
}

customElements.define('cart-drawer', CartDrawer);
